import { Listing } from '@smg-real-estate/hg-rets-schema';
import { ComposerTranslation } from 'vue-i18n';
import { PARKING_SPACE_CATEGORIES } from '../MetaInfo/MetaInfoHelper';
import {
  getListingCurrency,
  getListingDisplayPrice,
  getListingFormattedLivingSpace,
  getListingRooms,
  getStreetWithAddition,
} from './ListingData';

// Generic Functions for formatting
/**
 * Formats a decimal number as a fraction using Unicode fraction symbols.
 * Used for formatting the number of rooms.
 * @param value - The decimal number to format
 * @returns The formatted string with fraction symbols (e.g., 4.5 → 4½)
 */
export function formatNumberWithFractions(value: number): string {
  const wholePart = Math.floor(value);
  const decimalPart = value - wholePart;

  let fractionSymbol = '';
  if (Math.abs(decimalPart - 0.5) < 0.001) {
    fractionSymbol = '½';
  } else if (decimalPart > 0.001) {
    return value.toString();
  }

  // Return whole number if no decimal part
  if (fractionSymbol === '') {
    return wholePart.toString();
  }

  // Return whole part + fraction symbol
  return wholePart > 0 ? `${wholePart}${fractionSymbol}` : fractionSymbol;
}

/**
 * Builds a sanitized title string by filtering out empty values and joining non-empty title parts
 * @param titleParts - Array of title parts that may contain empty strings
 * @param separator - The separator to use between title parts (default: ' - ')
 */
function sanitizeListingTitle(titleParts: string[], separator = ' - '): string {
  const nonEmptyParts = titleParts.filter((part) => part && part.trim() !== '');
  return nonEmptyParts.join(separator);
}

// Floor
export function getListingFloor(listing?: Listing): number | undefined {
  return listing?.characteristics?.floor;
}

export function getListingFormattedFloor(listing: Listing, t: ComposerTranslation): string {
  const floor = getListingFloor(listing);

  if (typeof floor === 'undefined' || floor === 0) {
    return '';
  }

  return `${t('listingCharacteristic.floor')}: ${floor}`;
}

// Rooms and Category
/**
 * Maps Category enum to user readable, localized string
 */
export function getListingCategoryFormatted(listing: Listing, t: ComposerTranslation): string {
  const category = listing?.categories?.slice(-1)[0];

  return category ? t(`listingCategories.${category}`) : '';
}

/**
 * Gets the appropriate room term based on locale and room count
 * Handles singular/plural forms for different languages
 */
function getRoomTermByLocale(roomCount: number, t: ComposerTranslation, locale: string): string {
  const currentLocale = locale || 'en';

  // For German, "Zimmer" is both singular and plural
  if (currentLocale === 'de') {
    return t('ROOMS');
  }

  // Try platform-specific room terms with pluralization
  const platformRoomTerm = getPlatformRoomTerm(roomCount, t);
  if (platformRoomTerm) {
    return platformRoomTerm;
  }

  // Fallback to manual singular/plural logic
  return getManualRoomTerm(roomCount, locale, t);
}

/**
 * Attempts to get room term from platform-specific i18n keys
 */
function getPlatformRoomTerm(roomCount: number, t: ComposerTranslation): string {
  const platformRoomKey = 'recommendations.room';

  const roomTerm = t(platformRoomKey, roomCount);
  if (roomTerm && roomTerm !== platformRoomKey) {
    return roomTerm;
  }

  return '';
}

/**
 * Provides manual room term based on locale and count
 */
function getManualRoomTerm(roomCount: number, locale: string, t: ComposerTranslation): string {
  const isPlural = roomCount !== 1;

  switch (locale) {
    case 'en':
      return isPlural ? 'Rooms' : 'Room';
    case 'fr':
      return isPlural ? 'pièces' : 'pièce';
    case 'it':
      return isPlural ? 'locali' : 'locale';
    case 'de':
    default:
      return t('ROOMS');
  }
}

/**
 * Formats rooms and category based on locale-specific ordering
 */
function formatRoomsAndCategoryByLocale(
  locale: string,
  roomCount: string,
  roomTerm: string,
  categoryFormatted: string,
): string {
  if (locale === 'fr' || locale === 'it') {
    // French and Italian: Category first, then rooms
    const parts = [];
    if (categoryFormatted) parts.push(categoryFormatted);
    if (roomCount && roomTerm) parts.push(`${roomCount} ${roomTerm}`);
    return parts.length > 0 ? `${parts.join(' - ')} -` : '';
  }

  // English and German: Rooms first, then category
  const parts = [];
  if (roomCount && roomTerm) parts.push(`${roomCount} ${roomTerm}`);
  if (categoryFormatted) parts.push(categoryFormatted);
  return parts.length > 0 ? `${parts.join(' - ')} -` : '';
}

/**
 * Formats room count and category with locale-specific ordering and terminology
 * EN/DE: rooms roomTerm - category -
 * FR/IT: category - rooms roomTerm -
 *
 * Examples:
 * EN: "1 Room - Apartment -"
 * DE: "1 Zimmer - Wohnung -"
 * FR: "Appartement - 1 pièce -"
 * IT: "Appartamento - 1 locale -"
 */
export function getListingFormattedRoomsAndCategory(
  listing: Listing,
  t: ComposerTranslation,
  locale: string,
): string {
  const rooms = getListingRooms(listing);
  const category = listing?.categories?.slice(-1)[0];

  if ((typeof rooms === 'undefined' || !rooms) && !category) {
    return '';
  }

  // Get formatted room count with fractions
  const roomCount = typeof rooms !== 'undefined' && rooms ? formatNumberWithFractions(rooms) : '';

  // Get localized room term based on count (singular/plural)
  const roomTerm =
    typeof rooms !== 'undefined' && rooms ? getRoomTermByLocale(rooms, t, locale) : '';
  // Get formatted category
  const categoryFormatted = getListingCategoryFormatted(listing, t);
  // Get current locale
  const currentLocale = locale || 'en';

  return formatRoomsAndCategoryByLocale(currentLocale, roomCount, roomTerm, categoryFormatted);
}

// New Building
export function getIsListingNewBuilding(listing?: Listing): boolean | undefined {
  return listing?.characteristics?.isNewBuilding;
}
export function getIsListingNewBuildingFormatted(listing: Listing, t: ComposerTranslation): string {
  const isNewBuilding = getIsListingNewBuilding(listing);

  return isNewBuilding ? t('furnishings.fields.isNewBuilding') : '';
}

// Price
export function getListingPriceWithPreposition(listing: Listing, t: ComposerTranslation): string {
  const listingPrice = listing ? getListingDisplayPrice(listing) : '';

  return listingPrice
    ? `${t('metaInfo.pdp.preposition.for')} ${getListingCurrency(listing)} ${listingPrice}`
    : '';
}

// Location
export function getListingLocationWithPreposition(
  listing: Listing,
  t: ComposerTranslation,
): string {
  const address = (listing.address && getStreetWithAddition(listing.address)) || '';

  return address ? `${t('metaInfo.pdp.preposition.in')} ${address}` : '';
}

// Parking space
function hasParkingSpaceCategory(listing: Listing): boolean {
  return (
    listing.categories?.some((category) => new Set(PARKING_SPACE_CATEGORIES).has(category)) ?? false
  );
}

export function getListingFormattedChargingStation(
  listing: Listing,
  t: ComposerTranslation,
): string {
  if (!hasParkingSpaceCategory(listing)) return '';

  const hasChargingStation = listing?.characteristics?.hasChargingStation;

  return hasChargingStation ? t('furnishings.fields.hasChargingStation') : '';
}

export function getEnhancedListingTitle(
  listing: Listing,
  t: ComposerTranslation,
  locale: string,
): string {
  const location = getListingLocationWithPreposition(listing, t);
  const price = getListingPriceWithPreposition(listing, t);
  const meterage = getListingFormattedLivingSpace(listing);
  const newBuilding = getIsListingNewBuildingFormatted(listing, t);
  const floor = getListingFormattedFloor(listing, t);
  const roomWithCategory = getListingFormattedRoomsAndCategory(listing, t, locale);
  const parkingWithChargingStation = getListingFormattedChargingStation(listing, t);

  const titleParts = [location, price, meterage, newBuilding, parkingWithChargingStation, floor];

  // Build the title with only populated, non-empty listing information, using template
  const sanitizedTitle = sanitizeListingTitle(titleParts, ' - ');

  return `${roomWithCategory} ${sanitizedTitle}`;
}
